import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { getDestinationImage } from 'lib/search/searchUtils'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalContent from 'LuxKit/Modal/ModalContent'
import HeroImage from 'content/components/HeroImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  placeName: string
  placeId: string
  onAddDestination: (e: React.MouseEvent<any>) => void
  destinationSaved: boolean
}

const StyledHeroImage = styled(HeroImage)`
&& {
  min-height: unset;
  height: ${rem(167)};
}
`

function AddDestinationToggleModal(props: Props) {
  const { placeName, placeId, destinationSaved, onAddDestination } = props
  const intl = useIntl()
  const destinationImage = getDestinationImage(placeId)
  const modalElement = useModalElementContext()

  const handleSkip = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const handleAddDestination = useCallback<React.MouseEventHandler<any>>((e) => {
    onAddDestination(e)
    handleSkip()
  }, [onAddDestination, handleSkip])

  const alreadyInListHeading = intl.formatMessage({
    id: 'Dp8Ku9sSJA',
    defaultMessage: '{placeName} is already in your favourite destination list!',
    description: 'Heading shown when destination is already saved',
  }, { placeName })

  const addToPreferencesHeading = intl.formatMessage({
    id: 'k0iA5JczEp',
    defaultMessage: 'Do you want to add {placeName} to your travel preferences?',
    description: 'Heading asking user to add destination to preferences',
  }, { placeName })

  const alreadySavedMessage = intl.formatMessage({
    id: 'dRHlEPyYEU',
    defaultMessage: "You're all set to receive tailored recommendations and offers for it",
    description: 'Message shown when destination is already in favourites',
  })

  const getTailoredRecommendationsMessage = intl.formatMessage({
    id: '8UaIX+JSd9',
    defaultMessage: 'Get tailored recommendations to ensure you never miss an offer in your favourite destinations',
    description: 'Message encouraging user to add destination to preferences',
  })

  const okGotItText = intl.formatMessage({
    id: '6tVE3DW7bS',
    defaultMessage: 'Ok got it!',
    description: 'Button text to acknowledge destination is already saved',
  })

  const editPreferencesText = intl.formatMessage({
    id: 'JAfaMWroyA',
    defaultMessage: 'Edit preferences',
    description: 'Button text to navigate to edit travel preferences',
  })

  const addToWatchlistText = intl.formatMessage({
    id: 'pb1Jg7ww4Y',
    defaultMessage: 'Add to Watchlist',
    description: 'Button text to add destination to watchlist',
  })

  const skipForNowText = intl.formatMessage({
    id: 'UWrQLfAubJ',
    defaultMessage: 'Skip for now',
    description: 'Button text to skip adding destination',
  })

  return (
    <ModalBase>
      <ModalHeader title="" isOverlay />
      { destinationImage && <StyledHeroImage id={destinationImage} />}
      <ModalContent>
        <ModalBody>
          <VerticalSpacer gap={4}>
            <HeadingTextBlock variant="heading4" align="center" tabletAlign="start">
              {destinationSaved ? alreadyInListHeading : addToPreferencesHeading}
            </HeadingTextBlock>
            <BodyText variant="large" align="center" tabletAlign="start">
              {destinationSaved ? alreadySavedMessage : getTailoredRecommendationsMessage}
            </BodyText>
          </VerticalSpacer>
        </ModalBody>
        <ModalFooter>
          <Group direction="vertical" tabletDirection="horizontal-reverse">
            {destinationSaved && (
              <>
                <TextButton kind="primary" onClick={handleSkip}>{okGotItText}</TextButton>
                <TextButton kind="tertiary" to="/account/travel-preferences#destinations">{editPreferencesText}</TextButton>
              </>
            )}
            {!destinationSaved && (
              <>
                <TextButton kind="primary" onClick={handleAddDestination}>{addToWatchlistText}</TextButton>
                <TextButton kind="tertiary" onClick={handleSkip}>{skipForNowText}</TextButton>
              </>
            )}
          </Group>
        </ModalFooter>
      </ModalContent>
    </ModalBase>
  )
}

export default AddDestinationToggleModal
